import { PartnerDealCommitteeQueueItemResponse } from '@interfaces/partners/responses/partner-deal-committee.response';
import {
  CommitteeTag,
  isFinalVote,
} from '@app/features/dashboard/commercial/pages/deal-detail/components/deal-committee-panel/deal-committee-presenter';

/**
 * Textos e contagens da fila Comercial › Comitê de crédito (card 3789). Funções puras, testadas em
 * `credit-committee-presenter.spec.ts`. Quem decide se a pessoa é votante, se a contestação dela está
 * aberta e se o prazo venceu é o servidor (`isVoter`, `myContestOpen`, `isOverdue`).
 */

/** Filtro da fila. */
export type CreditCommitteeQueueFilter = 'open' | 'mine' | 'decided';

/** Os quatro números do topo da fila. */
export interface CreditCommitteeQueueKpis {
  awaitingMyVote: number;
  awaitingMyVoteOverdue: number;
  openContests: number;
  overdue: number;
  decided: number;
  approved: number;
  denied: number;
}

/**
 * O deal espera o voto de quem está vendo: é votante, ainda não deu voto final e não está com uma
 * contestação própria aguardando resposta (aí a bola está com o comercial).
 */
export function isAwaitingMyVote(item: PartnerDealCommitteeQueueItemResponse): boolean {
  return item.status === 'Open' && item.isVoter && !isFinalVote(item.myVote) && !item.myContestOpen;
}

/** Números do topo, a partir das duas listas que a tela carrega. */
export function computeQueueKpis(
  open: PartnerDealCommitteeQueueItemResponse[],
  decided: PartnerDealCommitteeQueueItemResponse[]
): CreditCommitteeQueueKpis {
  const mine = open.filter(isAwaitingMyVote);
  return {
    awaitingMyVote: mine.length,
    awaitingMyVoteOverdue: mine.filter(i => i.isOverdue).length,
    openContests: open.reduce((sum, i) => sum + (i.openContestCount ?? 0), 0),
    overdue: open.filter(i => i.isOverdue).length,
    decided: decided.length,
    approved: decided.filter(i => i.status === 'Approved').length,
    denied: decided.filter(i => i.status === 'Denied').length,
  };
}

/** As linhas do filtro escolhido. "Aguardando meu voto" vem da lista em comitê. */
export function filterQueue(
  filter: CreditCommitteeQueueFilter,
  open: PartnerDealCommitteeQueueItemResponse[],
  decided: PartnerDealCommitteeQueueItemResponse[]
): PartnerDealCommitteeQueueItemResponse[] {
  if (filter === 'decided') return decided;
  if (filter === 'mine') return open.filter(isAwaitingMyVote);
  return open;
}

/** "#1044 · 1 aprovação · 0 negação". */
export function describeQueueVotes(item: PartnerDealCommitteeQueueItemResponse): string {
  if (item.approvals === 0 && item.denials === 0) return `#${item.dealNumber} · nenhum voto`;
  const approvals = `${item.approvals} ${item.approvals === 1 ? 'aprovação' : 'aprovações'}`;
  const denials = `${item.denials} ${item.denials === 1 ? 'negação' : 'negações'}`;
  return `#${item.dealNumber} · ${approvals} · ${denials}`;
}

/** A situação da linha, do ponto de vista de quem está vendo. */
export function describeQueueSituation(item: PartnerDealCommitteeQueueItemResponse): CommitteeTag {
  switch (item.status) {
    case 'Approved':
      return { label: 'Aprovado', tone: 'success' };
    case 'Denied':
      return { label: 'Negado', tone: 'danger' };
    case 'Cancelled':
      return { label: 'Cancelado', tone: 'neutral' };
  }

  if (item.isVoter && item.myContestOpen) {
    return { label: 'Sua contestação está em aberto', tone: 'warning' };
  }
  if (item.isVoter && item.myVote === 'Approve') return { label: 'Você aprovou', tone: 'success' };
  if (item.isVoter && item.myVote === 'Deny') return { label: 'Você negou', tone: 'danger' };
  if (item.isOverdue) return { label: 'Prazo vencido', tone: 'danger' };
  if (item.isVoter) return { label: 'Aguardando seu voto', tone: 'neutral' };
  if (item.openContestCount > 0) return { label: 'Contestação em aberto', tone: 'warning' };
  return { label: 'Em votação', tone: 'info' };
}

/** Texto vazio de cada filtro, dizendo de onde os deals vêm. */
export function describeQueueEmpty(filter: CreditCommitteeQueueFilter): string {
  switch (filter) {
    case 'mine':
      return 'Nenhum voto pendente para você. Quando um deal for enviado ao comitê, você recebe o pedido no sino e por e-mail.';
    case 'decided':
      return 'Nenhum comitê decidido nos últimos 30 dias.';
    default:
      return 'Nenhum deal em votação agora. Os deals entram aqui quando o comercial os envia ao comitê, na etapa Análise.';
  }
}
